import React, { useEffect, useState } from "react";
import { Zap, AlertTriangle, RefreshCw, ShoppingCart, PlusCircle, X, HardDrive } from "lucide-react";
import { createAuthHeaders } from "@/lib/auth-utils";
import CreditRechargeModal from "@/components/modals/CreditRechargeModal";
import { IS_CREDIT_RECHARGE_ENABLED } from "@/lib/config/featureFlags";

interface CreditInfo {
  total_balance: number;
  plan_credits_remaining: number;
  topup_credits_remaining: number;
  max_credits?: number;
  last_reset_at?: string;
}

interface StorageInfo {
  total_mb: number;
  total_gb: number;
  quota_gb: number;
  percentage: number;
  breakdown: {
    expedientes_mb: number;
    contracts_mb: number;
    research_mb: number;
    drafts_mb: number;
    chatbot_mb: number;
  };
}

interface CreditBadgeProps {
  variant?: "badge" | "card";
  maxCredits?: number;
}

// Shared in-memory cache and inflight promises so desktop & mobile badges do not duplicate requests
let _cachedCredits: any = null;
let _creditsPromise: Promise<any> | null = null;
let _lastCreditsTime = 0;

let _cachedStorage: any = null;
let _storagePromise: Promise<any> | null = null;
let _lastStorageTime = 0;

export const CreditBadge: React.FC<CreditBadgeProps> = ({ variant = "badge", maxCredits = 180 }) => {
  const [credits, setCredits] = useState<CreditInfo | null>(null);
  const [storage, setStorage] = useState<StorageInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showRechargeModal, setShowRechargeModal] = useState<boolean>(false);

  const backendUrl = import.meta.env.VITE_BACKEND_URL || "http://localhost:8001";

  const fetchCredits = async (force = false) => {
    const now = Date.now();
    if (!force && _cachedCredits && (now - _lastCreditsTime < 60000)) {
      setCredits(_cachedCredits);
      setLoading(false);
      return;
    }
    if (_creditsPromise) {
      const data = await _creditsPromise;
      if (data) setCredits(data);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      _creditsPromise = (async () => {
        const authHeaders = await createAuthHeaders();
        const res = await fetch(`${backendUrl}/api/v1/credits/balance`, {
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
        });
        if (res.ok) {
          const data = await res.json();
          _cachedCredits = data;
          _lastCreditsTime = Date.now();
          return data;
        }
        return null;
      })();
      const data = await _creditsPromise;
      if (data) setCredits(data);
    } catch (e) {
      console.error("Error fetching credits balance:", e);
    } finally {
      _creditsPromise = null;
      setLoading(false);
    }
  };

  const fetchStorage = async (force = false) => {
    const now = Date.now();
    if (!force && _cachedStorage && (now - _lastStorageTime < 300000)) {
      setStorage(_cachedStorage);
      return;
    }
    if (_storagePromise) {
      const data = await _storagePromise;
      if (data) setStorage(data);
      return;
    }

    try {
      _storagePromise = (async () => {
        const authHeaders = await createAuthHeaders();
        const res = await fetch(`${backendUrl}/api/v1/user/storage-usage`, {
          headers: {
            "Content-Type": "application/json",
            ...authHeaders,
          },
        });
        if (res.ok) {
          const data = await res.json();
          _cachedStorage = data;
          _lastStorageTime = Date.now();
          return data;
        }
        return null;
      })();
      const data = await _storagePromise;
      if (data) setStorage(data);
    } catch (e) {
      console.error("Error fetching storage usage:", e);
    } finally {
      _storagePromise = null;
    }
  };

  useEffect(() => {
    const onCreditsUpdated = () => {
      fetchCredits(true);
    };

    const onStorageUpdated = () => {
      fetchStorage(true);
    };

    fetchCredits();
    fetchStorage();

    // Passive fallback interval: 5 minutes (300,000 ms) instead of 30 seconds
    const interval = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      fetchCredits();
    }, 300000);

    window.addEventListener("credits-updated", onCreditsUpdated);
    window.addEventListener("storage-updated", onStorageUpdated);

    return () => {
      clearInterval(interval);
      window.removeEventListener("credits-updated", onCreditsUpdated);
      window.removeEventListener("storage-updated", onStorageUpdated);
    };
  }, []);

  const balance = credits?.total_balance ?? 180;
  const effectiveMaxCredits = credits?.max_credits || maxCredits || 180;
  const percentage = Math.min(100, Math.max(0, Math.round((balance / effectiveMaxCredits) * 100)));

  // Storage calculations
  const storageMb = storage?.total_mb ?? 0;
  const storageQuotaGb = storage?.quota_gb ?? 5.0;
  const storagePct = storage?.percentage ?? 0;

  const formatStorage = (mb: number) => {
    if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
    return `${mb.toFixed(1)} MB`;
  };

  // Dynamic status color coding
  // Verde (>50%), Amarillo (25-50%), Rojo (<25%)
  let statusTheme = {
    bg: "bg-emerald-50 dark:bg-emerald-950/40 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-400",
    bar: "bg-emerald-500",
    text: "Verde (>50%): Todo normal",
  };

  if (percentage < 25) {
    statusTheme = {
      bg: "bg-rose-50 dark:bg-rose-950/40 border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-400 animate-pulse",
      bar: "bg-rose-500",
      text: "Rojo (<25%): Quedan pocos créditos",
    };
  } else if (percentage < 50) {
    statusTheme = {
      bg: "bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400",
      bar: "bg-amber-500",
      text: "Amarillo (25-50%): Créditos agotándose",
    };
  }

  // Storage color coding
  let storageBarColor = "bg-sky-500";
  let storageBgColor = "bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-800";
  if (storagePct >= 90) {
    storageBarColor = "bg-rose-500";
    storageBgColor = "bg-rose-50 dark:bg-rose-950/30 border-rose-200 dark:border-rose-800";
  } else if (storagePct >= 70) {
    storageBarColor = "bg-amber-500";
    storageBgColor = "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800";
  }

  // Renewal date text calculation
  const getRenewalDate = () => {
    const today = new Date();
    const renewalDay = 15; // Standard cycle day
    let month = today.getMonth();
    if (today.getDate() >= renewalDay) {
      month += 1;
    }
    const date = new Date(today.getFullYear(), month, renewalDay);
    return date.toLocaleDateString("es-ES", { day: "numeric", month: "long" });
  };

  if (loading && !credits) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-gray-400 bg-gray-100 dark:bg-gray-800 px-3 py-1.5 rounded-xl animate-pulse">
        <Zap className="w-3.5 h-3.5" />
        <span>Cargando créditos...</span>
      </div>
    );
  }

  // Card Variant (Full Dashboard/Sidebar Widget)
  if (variant === "card") {
    return (
      <div className="space-y-2.5 w-full">
        {/* Credits Card */}
        <div className={`p-3.5 rounded-2xl border ${statusTheme.bg} shadow-sm space-y-2.5 transition-all hover:shadow-md`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 font-bold text-xs">
              <Zap className="w-4 h-4 text-amber-500 fill-amber-500" />
              <span>{balance} / {effectiveMaxCredits} créditos IA</span>
            </div>
            {IS_CREDIT_RECHARGE_ENABLED && (
              <button
                type="button"
                onClick={() => setShowRechargeModal(true)}
                className="flex items-center gap-1 text-[11px] font-bold underline hover:opacity-80 transition-opacity"
              >
                <PlusCircle className="w-3 h-3" />
                <span>+ Recargar</span>
              </button>
            )}
          </div>

          {/* Progress Bar */}
          <div className="space-y-1">
            <div className="w-full bg-gray-200 dark:bg-gray-700 h-2.5 rounded-full overflow-hidden flex">
              <div
                className={`h-full ${statusTheme.bar} transition-all duration-500`}
                style={{ width: `${percentage}%` }}
              />
            </div>
            <div className="flex justify-between items-center text-[10px] opacity-80 font-medium">
              <span>Se renuevan el {getRenewalDate()}</span>
              <span className="font-bold">{percentage}%</span>
            </div>
          </div>
        </div>

        {/* Storage Card */}
        <div className={`p-3.5 rounded-2xl border ${storageBgColor} shadow-sm space-y-2.5 transition-all hover:shadow-md`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 font-bold text-xs text-sky-700 dark:text-sky-400">
              <HardDrive className="w-4 h-4" />
              <span>{formatStorage(storageMb)} / {storageQuotaGb} GB</span>
            </div>
            <span className="text-[10px] font-bold text-sky-600 dark:text-sky-400 opacity-80">
              {storagePct.toFixed(1)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden">
            <div
              className={`h-full ${storageBarColor} transition-all duration-500`}
              style={{ width: `${Math.min(storagePct, 100)}%` }}
            />
          </div>
        </div>
      </div>
    );
  }

  // Header / Badge Variant (Pill button with modal trigger)
  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setShowModal(true)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setShowModal(true); }}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-all hover:scale-105 shadow-sm cursor-pointer ${statusTheme.bg}`}
        title="Ver indicador de Créditos de IA"
      >
        <Zap className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
        <span>{balance} / {effectiveMaxCredits}</span>
        <span className="opacity-75 hidden sm:inline">créditos IA</span>

        <div className="w-12 bg-gray-200 dark:bg-gray-700 h-2 rounded-full overflow-hidden ml-0.5 hidden md:block">
          <div className={`h-full ${statusTheme.bar} transition-all duration-500`} style={{ width: `${percentage}%` }} />
        </div>

        {IS_CREDIT_RECHARGE_ENABLED && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowRechargeModal(true); }}
            className="ml-1 text-[10px] font-bold bg-amber-500 hover:bg-amber-600 text-white px-2 py-0.5 rounded-lg transition-colors flex items-center gap-0.5"
          >
            + Recargar
          </button>
        )}
      </div>

      {/* Modal de Detalle Completo de Créditos */}
      {showModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
          onClick={() => setShowModal(false)}
        >
          <div 
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-5 relative max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Botón X destacado en la esquina superior derecha */}
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 z-20 p-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-full transition-all shadow-md focus:outline-none flex items-center justify-center"
              aria-label="Cerrar modal"
              title="Cerrar modal"
            >
              <X className="w-5 h-5 stroke-[2.5]" />
            </button>

            <div className="flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 pb-4 pr-10">
              <div className="p-3 bg-amber-100 dark:bg-amber-950/60 rounded-2xl text-amber-600">
                <Zap className="w-6 h-6 fill-amber-500" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Indicador de Créditos de IA</h3>
                <p className="text-xs text-gray-500">Gestión transparente de cuota PRO en la nube</p>
              </div>
            </div>

            {/* Widget de Tarjeta Interno */}
            <div className={`p-4 rounded-2xl border ${statusTheme.bg} space-y-3`}>
              <div className="flex items-center justify-between font-bold text-sm">
                <span>⚡ {balance} / {effectiveMaxCredits} créditos IA</span>
                <span className="text-xs bg-white/80 dark:bg-black/30 px-2 py-0.5 rounded-full">{percentage}%</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 h-3 rounded-full overflow-hidden">
                <div className={`h-full ${statusTheme.bar} transition-all duration-500`} style={{ width: `${percentage}%` }} />
              </div>
              <div className="flex justify-between items-center text-xs opacity-90 font-medium">
                <span>Se renuevan el {getRenewalDate()}</span>
                <button
                  onClick={() => { fetchCredits(); fetchStorage(); }}
                  className="p-1 hover:bg-black/10 rounded-lg transition-colors"
                  title="Actualizar saldo"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Desglose de Saldos */}
            <div className="text-xs space-y-2 bg-gray-50 dark:bg-gray-800/40 p-3.5 rounded-xl border border-gray-100 dark:border-gray-800">
              <div className="flex justify-between text-gray-600 dark:text-gray-400">
                <span>Créditos del Plan Mensual:</span>
                <span className="font-bold text-gray-900 dark:text-white">{credits?.plan_credits_remaining ?? 0}</span>
              </div>
              <div className="flex justify-between text-gray-600 dark:text-gray-400">
                <span>Créditos de Recargas Adicionales:</span>
                <span className="font-bold text-gray-900 dark:text-white">{credits?.topup_credits_remaining ?? 0}</span>
              </div>
            </div>

            {/* Almacenamiento */}
            <div className={`p-4 rounded-2xl border ${storageBgColor} space-y-3`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 font-bold text-sm text-sky-700 dark:text-sky-300">
                  <HardDrive className="w-5 h-5" />
                  <span>Almacenamiento</span>
                </div>
                <span className="text-xs font-bold bg-white/80 dark:bg-black/30 px-2 py-0.5 rounded-full text-sky-700 dark:text-sky-300">
                  {storagePct.toFixed(1)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 h-3 rounded-full overflow-hidden">
                <div className={`h-full ${storageBarColor} transition-all duration-500`} style={{ width: `${Math.min(storagePct, 100)}%` }} />
              </div>
              <div className="flex items-center justify-between text-xs font-medium text-sky-600 dark:text-sky-400">
                <span>{formatStorage(storageMb)} de {storageQuotaGb} GB usados</span>
              </div>

              {/* Storage Breakdown */}
              {storage?.breakdown && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px] text-sky-700/80 dark:text-sky-400/80 pt-1 border-t border-sky-200/50 dark:border-sky-800/50">
                  <div className="flex justify-between">
                    <span>Expedientes:</span>
                    <span className="font-semibold">{storage.breakdown.expedientes_mb} MB</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Contratos:</span>
                    <span className="font-semibold">{storage.breakdown.contracts_mb} MB</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Investigación:</span>
                    <span className="font-semibold">{storage.breakdown.research_mb} MB</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Borradores:</span>
                    <span className="font-semibold">{storage.breakdown.drafts_mb} MB</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Chat Legal:</span>
                    <span className="font-semibold">{storage.breakdown.chatbot_mb} MB</span>
                  </div>
                </div>
              )}

              {storagePct >= 80 && (
                <button
                  type="button"
                  className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-bold py-2.5 px-4 rounded-xl text-xs hover:shadow-lg hover:from-sky-600 hover:to-blue-700 transition-all"
                  onClick={() => {
                    // TODO: Implement storage purchase flow
                    alert("Próximamente: compra de almacenamiento adicional.");
                  }}
                >
                  <HardDrive className="w-4 h-4" />
                  Comprar Almacenamiento Adicional
                </button>
              )}
            </div>

            {/* Tabla Informativa de Consumo */}
            <div className="bg-amber-50/60 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 rounded-xl p-3.5 text-xs text-amber-800 dark:text-amber-300 space-y-1.5">
              <div className="font-bold flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                Consumo por Funcionalidad:
              </div>
              <ul className="list-disc list-inside space-y-1 opacity-90 pl-1">
                <li><strong>1 Consulta SarAI:</strong> 1 Crédito</li>
                <li><strong>Redacción / Contratos:</strong> 3 Créditos</li>
                <li><strong>Estrategia Jurídica PRO:</strong> 5 Créditos</li>
                <li><strong>Vigilancia y Scrapper:</strong> 0 Créditos (100% Gratis local)</li>
              </ul>
            </div>

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className={`py-3 px-4 rounded-xl text-xs font-bold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${!IS_CREDIT_RECHARGE_ENABLED ? "w-full" : ""}`}
              >
                Cerrar
              </button>
              {IS_CREDIT_RECHARGE_ENABLED && (
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setShowRechargeModal(true); }}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold py-3 px-4 rounded-xl text-xs hover:shadow-lg hover:from-blue-700 hover:to-indigo-700 transition-all"
                >
                  <ShoppingCart className="w-4 h-4" />
                  Comprar Recarga de Créditos
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <CreditRechargeModal
        isOpen={showRechargeModal}
        onClose={() => setShowRechargeModal(false)}
      />
    </>
  );
};
